import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DateSelect from '../components/ui/DateSelect';
import Icons from '../components/Icons';
import BulkActionBar from '../components/inventory/BulkActionBar';
import WorkspaceKpis from '../components/workspace/WorkspaceKpis';
import SearchableSelect from '../components/ui/SearchableSelect';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import { useStudentStore } from '../store/StudentStore';
import { useSettings } from '../store/SettingsContext';
import { getAttendanceSignals, normalizeAttendanceStatus } from '../components/attendance/attendanceUtils';
import AttendanceService from '../services/AttendanceService';
import StaffService from '../services/StaffService';
import ProgrammeService from '../services/ProgrammeService';
import ActivityService from '../services/ActivityService';
import JournalService from '../services/JournalService';
import ExternalMessageService from '../services/ExternalMessageService';
import AssignmentService from '../services/AssignmentService';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { buildZip } from '../utils/zipBuilder';

const reportTypes = [
  { id: 'attendance', label: 'Attendance' },
  { id: 'incidents', label: 'Incidents' },
  { id: 'safeguarding', label: 'Safeguarding' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'programmes', label: 'Programmes/Activities' }
];

const dateRanges = [
  { id: '7', label: 'Last 7 days', days: 7 },
  { id: '30', label: 'Last 30 days', days: 30 },
  { id: 'term', label: 'Term (90 days)', days: 90 },
  { id: 'custom', label: 'Custom' }
];

const riskOrder = { critical: 3, high: 2, medium: 1, low: 0 };

const XLSX_CELL_CHAR_LIMIT = 32767;

const clampExcelCell = (text) => {
  if (text.length <= XLSX_CELL_CHAR_LIMIT) return text;
  const suffix = '...[truncated]';
  return `${text.slice(0, XLSX_CELL_CHAR_LIMIT - suffix.length)}${suffix}`;
};

const sanitizeExportValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeExportValue(entry))
      .filter(Boolean)
      .join('; ');
  }
  if (typeof value === 'object') {
    const json = JSON.stringify(value, (key, item) => {
      if (typeof item === 'string' && item.startsWith('data:')) {
        return '[binary omitted]';
      }
      return item;
    });
    return clampExcelCell(json || '');
  }
  return clampExcelCell(String(value));
};

const normalizeCell = (value) => {
  return sanitizeExportValue(value);
};

const buildSheet = (headers, rows, emptyMessage) => {
  const safeRows = rows.length
    ? rows
    : [[emptyMessage, ...Array(Math.max(headers.length - 1, 0)).fill('')]];
  return XLSX.utils.aoa_to_sheet([headers, ...safeRows].map((row) => row.map(normalizeCell)));
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

const dataUrlToBytes = (value) => {
  if (!value || typeof value !== 'string' || !value.startsWith('data:')) return null;
  const commaIndex = value.indexOf(',');
  if (commaIndex < 0) return null;
  const meta = value.slice(0, commaIndex);
  const payload = value.slice(commaIndex + 1);
  const isBase64 = /;base64/i.test(meta);
  try {
    if (isBase64) {
      const binary = window.atob(payload);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    }
    return new TextEncoder().encode(decodeURIComponent(payload));
  } catch (error) {
    return null;
  }
};

const buildSafeguardingEvents = (student) => ([
  ...(student.safeguardingEvents || []).map((event) => ({ ...event, categoryLabel: 'Safeguarding' })),
  ...(student.medicalEvents || []).map((event) => ({ ...event, categoryLabel: 'Medical' })),
  ...(student.policeEvents || []).map((event) => ({ ...event, categoryLabel: 'Police' }))
]);

const isActiveSafeguarding = (event) => event.status === 'open' || event.status === 'monitoring';

const getDateRangeBounds = (rangeId, customRange) => {
  const now = new Date();
  if (rangeId === 'custom' && customRange.start && customRange.end) {
    return {
      start: new Date(customRange.start),
      end: new Date(customRange.end)
    };
  }
  const range = dateRanges.find((item) => item.id === rangeId);
  if (!range || !range.days) {
    return { start: null, end: null };
  }
  const end = new Date(now);
  const start = new Date(now);
  start.setDate(now.getDate() - range.days);
  return { start, end };
};

const shouldIncludeDate = (dateValue, rangeId, customRange) => {
  if (!dateValue) return false;
  const date = parseDateValue(dateValue);
  if (!date) return false;
  const { start, end } = getDateRangeBounds(rangeId, customRange);
  if (!start || !end) return true;
  return date >= start && date <= end;
};

const defaultFilters = {
  programmes: [],
  students: [],
  staff: '',
  risk: 'all',
  status: 'all'
};

const normalizeRunSettings = (input = {}, fallbackReportType = 'attendance') => ({
  reportType: input.reportType || fallbackReportType,
  dateRange: input.dateRange || '30',
  customRange: input.customRange || { start: '', end: '' },
  filters: {
    ...defaultFilters,
    ...(input.filters || {})
  },
  showExceptionsOnly: input.showExceptionsOnly ?? true,
  actionRequiredOnly: input.actionRequiredOnly ?? true,
  incidentGroupBy: input.incidentGroupBy || 'date'
});

const getStoredSettings = (type) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`report_filters_${type}`);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const storeSettings = (type, settings) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(`report_filters_${type}`, JSON.stringify(settings));
};

const loadTemplates = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem('report_templates');
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
};

const saveTemplates = (templates) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem('report_templates', JSON.stringify(templates));
};

const loadRuns = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem('report_runs');
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
};

const saveRuns = (runs) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem('report_runs', JSON.stringify(runs));
};

export default function ReportsPage() {
  const { students, incidents, inventoryItems, inventoryController } = useStudentStore();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const attendanceService = useMemo(() => new AttendanceService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const activityService = useMemo(() => new ActivityService(), []);
  const journalService = useMemo(() => new JournalService(), []);
  const externalMessageService = useMemo(() => new ExternalMessageService(), []);
  const assignmentService = useMemo(() => new AssignmentService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const programmes = useMemo(() => programmeService.list(), [programmeService]);
  const activities = useMemo(() => activityService.list(), [activityService]);

  const defaultReportRange = settings.defaultReportRange || '30';
  const [reportType, setReportType] = useState('attendance');
  const [dateRange, setDateRange] = useState(defaultReportRange);
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [filters, setFilters] = useState(defaultFilters);
  const [activeKpi, setActiveKpi] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeResultId, setActiveResultId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showExceptionsOnly, setShowExceptionsOnly] = useState(true);
  const [actionRequiredOnly, setActionRequiredOnly] = useState(true);
  const [incidentGroupBy, setIncidentGroupBy] = useState('date');
  const [savedTemplates, setSavedTemplates] = useState(() => loadTemplates());
  const [recentRuns, setRecentRuns] = useState(() => loadRuns());
  const [savedTab, setSavedTab] = useState('saved');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [reportError, setReportError] = useState('');
  const [hasRunReport, setHasRunReport] = useState(false);
  const [exportScope, setExportScope] = useState('all');
  const [exportStudentId, setExportStudentId] = useState('');
  const [exportSections, setExportSections] = useState({
    students: true,
    attendance: true,
    incidents: true,
    safeguarding: true,
    compliance: true,
    inventory: true,
    programmes: true
  });
  const [includeAttachments, setIncludeAttachments] = useState(false);
  const pendingTemplateRunRef = useRef(null);

  const studentOptions = useMemo(
    () => students.map((student) => ({ value: student.id, label: student.name, meta: student.reference || student.id })),
    [students]
  );
  const programmeOptions = useMemo(
    () => programmes.map((programme) => ({ value: programme.name, label: programme.name })),
    [programmes]
  );
  const staffOptions = useMemo(
    () => staffList.map((staff) => ({ value: staff.name, label: staff.name, meta: staff.role || '' })),
    [staffList]
  );

  const exportSectionOptions = [
    { id: 'students', label: 'Student profiles' },
    { id: 'attendance', label: 'Attendance logs' },
    { id: 'incidents', label: 'Incidents' },
    { id: 'safeguarding', label: 'Safeguarding' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'inventory', label: 'Inventory' },
    { id: 'programmes', label: 'Programmes & activities' }
  ];

  const resetExportState = () => {
    setExportScope('all');
    setExportStudentId('');
    setExportSections({
      students: true,
      attendance: true,
      incidents: true,
      safeguarding: true,
      compliance: true,
      inventory: true,
      programmes: true
    });
    setIncludeAttachments(false);
  };

  const allExportSelected = exportSectionOptions.every((option) => exportSections[option.id]);
  const toggleExportSection = (id) => {
    setExportSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const setAllExportSections = (value) => {
    setExportSections(
      exportSectionOptions.reduce((acc, option) => {
        acc[option.id] = value;
        return acc;
      }, {})
    );
  };

  useEffect(() => {
    const stored = getStoredSettings(reportType);
    if (stored) {
      setDateRange(stored.dateRange || defaultReportRange);
      setCustomRange(stored.customRange || { start: '', end: '' });
      setFilters(stored.filters || defaultFilters);
      setShowExceptionsOnly(stored.showExceptionsOnly ?? true);
      setActionRequiredOnly(stored.actionRequiredOnly ?? true);
      setIncidentGroupBy(stored.incidentGroupBy || 'date');
    } else {
      setDateRange(defaultReportRange);
      setCustomRange({ start: '', end: '' });
      setFilters(defaultFilters);
      setShowExceptionsOnly(true);
      setActionRequiredOnly(true);
      setIncidentGroupBy('date');
    }
    setResults([]);
    setActiveKpi(null);
    setSelectedIds(new Set());
    setActiveResultId(null);
    setDrawerOpen(false);
    setReportError('');
    setHasRunReport(false);
  }, [reportType, defaultReportRange]);

  useEffect(() => {
    storeSettings(reportType, {
      dateRange,
      customRange,
      filters,
      showExceptionsOnly,
      actionRequiredOnly,
      incidentGroupBy
    });
  }, [reportType, dateRange, customRange, filters, showExceptionsOnly, actionRequiredOnly, incidentGroupBy]);

  useEffect(() => {
    saveTemplates(savedTemplates);
  }, [savedTemplates]);

  useEffect(() => {
    saveRuns(recentRuns);
  }, [recentRuns]);

  const kpiItems = useMemo(() => {
    const flagged = results.filter((row) => row.flagged || row.isOverdue || row.isCritical).length;
    return [
      { id: 'saved', label: 'Saved Reports', value: savedTemplates.length },
      { id: 'recent', label: 'Recently Run', value: recentRuns.length },
      { id: 'flagged', label: 'Flagged / High Risk Outputs', value: flagged, badge: { label: 'Review', variant: 'danger' } },
      { id: 'scheduled', label: 'Scheduled Reports', value: 0 }
    ];
  }, [savedTemplates.length, recentRuns.length, results]);

  const handleKpiSelect = (id) => {
    setActiveKpi(id);
    if (id === 'saved') setSavedTab('saved');
    if (id === 'recent') setSavedTab('recent');
  };

  const applyReportSettings = useCallback((nextSettings) => {
    setDateRange(nextSettings.dateRange || defaultReportRange);
    setCustomRange(nextSettings.customRange || { start: '', end: '' });
    setFilters({ ...defaultFilters, ...(nextSettings.filters || {}) });
    setShowExceptionsOnly(nextSettings.showExceptionsOnly ?? true);
    setActionRequiredOnly(nextSettings.actionRequiredOnly ?? true);
    setIncidentGroupBy(nextSettings.incidentGroupBy || 'date');
  }, [defaultReportRange]);

  const validateRunSettings = (settingsToValidate) => {
    if (settingsToValidate.dateRange !== 'custom') return '';
    const { start, end } = settingsToValidate.customRange || {};
    if (!start || !end) return 'Custom range requires both start and end dates.';
    const startDate = parseDateValue(start);
    const endDate = parseDateValue(end);
    if (!startDate || !endDate) return 'Custom range dates are invalid.';
    if (startDate > endDate) return 'Custom range start date must be before end date.';
    return '';
  };

  const runReport = useCallback((overrideSettings = null) => {
    const settings = normalizeRunSettings(overrideSettings || {
      reportType,
      dateRange,
      customRange,
      filters,
      showExceptionsOnly,
      actionRequiredOnly,
      incidentGroupBy
    }, reportType);
    const validationError = validateRunSettings(settings);
    if (validationError) {
      setReportError(validationError);
      setResults([]);
      setHasRunReport(false);
      return false;
    }
    setReportError('');

    const { reportType: type } = settings;
    const rangeId = settings.dateRange;
    const custom = settings.customRange;
    const filterSettings = settings.filters;

    let nextResults = [];

    if (type === 'attendance') {
      students.forEach((student) => {
        if (filterSettings.students.length && !filterSettings.students.includes(student.id)) return;
        if (filterSettings.risk !== 'all' && student.riskLevel !== filterSettings.risk) return;
        const logs = student.attendance?.logs || [];
        const signals = getAttendanceSignals(student);
        logs.forEach((log) => {
          if (!shouldIncludeDate(log.date || log.recordedAt, rangeId, custom)) return;
          if (filterSettings.programmes.length && !filterSettings.programmes.includes(log.programme)) return;
          const status = normalizeAttendanceStatus(log.status);
          if (filterSettings.status !== 'all' && filterSettings.status !== status) return;
          const isException = status !== 'attended' || attendanceService.isBelowThreshold(student);
          if (settings.showExceptionsOnly && !isException) return;
          nextResults.push({
            id: `att-${student.id}-${log.id || log.date || Math.random()}`,
            studentId: student.id,
            studentName: student.name,
            reference: student.reference,
            programme: log.programme || '—',
            date: log.date || log.recordedAt || '',
            status,
            attendanceRate: student.attendance?.rate || 0,
            risk: student.riskLevel,
            notes: log.notes || '',
            flagged: isException,
            signals
          });
        });
      });
    }

    if (type === 'incidents') {
      const now = new Date();
      nextResults = incidents
        .filter((incident) => shouldIncludeDate(incident.date, rangeId, custom))
        .filter((incident) => {
          if (filterSettings.status !== 'all' && incident.status !== filterSettings.status) return false;
          if (filterSettings.risk !== 'all' && incident.severity !== filterSettings.risk) return false;
          if (filterSettings.staff && incident.assignedKeyWorker !== filterSettings.staff) return false;
          if (filterSettings.students.length) {
            const studentIds = incident.participants
              .filter((p) => p.type === 'student')
              .map((p) => p.id);
            if (!studentIds.some((id) => filterSettings.students.includes(id))) return false;
          }
          return true;
        })
        .map((incident) => {
          const followUpOverdue =
            incident.followUpRequired &&
            incident.followUpDate &&
            parseDateValue(incident.followUpDate) < now &&
            incident.status !== 'closed';
          const studentsList = incident.participants
            .filter((p) => p.type === 'student')
            .map((p) => p.name)
            .join(', ');
          return {
            id: incident.id,
            type: incident.type,
            severity: incident.severity,
            status: incident.status,
            date: incident.date,
            followUpDate: incident.followUpDate || '',
            followUpOverdue,
            assignedKeyWorker: incident.assignedKeyWorker || 'Unassigned',
            students: studentsList,
            flagged: followUpOverdue || ['high', 'critical'].includes(incident.severity)
          };
        })
        .sort((a, b) => {
          const severityDelta = (riskOrder[b.severity] || 0) - (riskOrder[a.severity] || 0);
          if (severityDelta !== 0) return severityDelta;
          if (a.followUpOverdue !== b.followUpOverdue) return a.followUpOverdue ? -1 : 1;
          if (a.status !== b.status) return a.status === 'open' ? -1 : 1;
          return (b.date || '').localeCompare(a.date || '');
        });
    }

    if (type === 'safeguarding') {
      nextResults = students.flatMap((student) =>
        buildSafeguardingEvents(student)
          .filter((event) => shouldIncludeDate(event.date, rangeId, custom))
          .filter((event) => {
            if (filterSettings.status !== 'all' && event.status !== filterSettings.status) return false;
            if (filterSettings.risk !== 'all' && event.severity !== filterSettings.risk) return false;
            if (filterSettings.staff && event.assignedOwner !== filterSettings.staff) return false;
            if (filterSettings.students.length && !filterSettings.students.includes(student.id)) return false;
            return true;
          })
          .map((event) => {
            const reviewDate = parseDateValue(event.reviewDate);
            const overdue = reviewDate && reviewDate < new Date() && isActiveSafeguarding(event);
            const unassigned = !event.assignedOwner;
            const critical = ['high', 'critical'].includes(event.severity);
            const actionRequired = overdue || unassigned || critical;
            if (settings.actionRequiredOnly && !actionRequired) return null;
            return {
              id: `saf-${student.id}-${event.id}`,
              type: event.type,
              category: event.categoryLabel || event.category,
              severity: event.severity,
              status: event.status,
              studentId: student.id,
              studentName: student.name,
              owner: event.assignedOwner || 'Unassigned',
              reviewDate: event.reviewDate || '',
              flagged: actionRequired,
              isOverdue: overdue,
              isCritical: critical
            };
          })
      ).filter(Boolean);
    }

    if (type === 'compliance') {
      nextResults = students.flatMap((student) => {
        const docs = [...(student.documents || []), ...(student.certificates || [])];
        if (!docs.length) {
          return [{
            id: `comp-${student.id}-missing`,
            item: 'Missing documents',
            area: 'Compliance',
            status: 'missing',
            studentId: student.id,
            studentName: student.name,
            owner: student.keyWorker?.name || 'Unassigned',
            dueDate: '',
            evidence: 'Missing',
            flagged: true
          }];
        }
        return docs.map((doc) => {
          const dueDate = doc.dueDate || doc.metadata?.dueDate;
          const expiryDate = doc.expiryDate || doc.metadata?.expiryDate;
          const date = parseDateValue(dueDate || expiryDate);
          let status = 'compliant';
          if (!date) status = 'missing';
          if (date && date < new Date()) status = 'overdue';
          const soon = new Date();
          soon.setDate(soon.getDate() + 30);
          if (date && date >= new Date() && date <= soon) status = 'due-soon';
          if (filterSettings.status !== 'all' && filterSettings.status !== status) return null;
          if (filterSettings.students.length && !filterSettings.students.includes(student.id)) return null;
          return {
            id: `comp-${student.id}-${doc.id || doc.name}`,
            item: doc.name,
            area: doc.type || doc.category || 'Policy',
            status,
            studentId: student.id,
            studentName: student.name,
            owner: student.keyWorker?.name || 'Unassigned',
            dueDate: dueDate || expiryDate || '',
            evidence: doc.versions?.length || doc.fileName ? 'Uploaded' : 'Missing',
            flagged: ['missing', 'overdue', 'due-soon'].includes(status)
          };
        }).filter(Boolean);
      });
    }

    if (type === 'inventory') {
      nextResults = inventoryItems
        .map((item) => {
          const assignment = inventoryController.getActiveAssignment(item);
          const dueStatus = inventoryController.getDueStatus(item);
          const status = item.status || 'available';
          const unassigned = !assignment;
          const overdue = dueStatus.isOverdue;
          const underRepair = status === 'under_repair';
          const flagged = overdue || unassigned || underRepair;
          if (filterSettings.status !== 'all') {
            if (filterSettings.status === 'overdue' && !overdue) return null;
            if (filterSettings.status === 'under_repair' && !underRepair) return null;
            if (filterSettings.status === 'unassigned' && !unassigned) return null;
            if (filterSettings.status === 'checked_out' && !assignment) return null;
          }
          return {
            id: item.id,
            item: item.name,
            status,
            assignment: assignment ? assignment.assignedToName || assignment.assignedToId : 'Unassigned',
            dueDate: assignment?.dueDate || '',
            condition: item.condition || 'Good',
            flagged,
            isOverdue: overdue
          };
        })
        .filter(Boolean);
    }

    if (type === 'programmes') {
      const programmeStats = programmes.map((programme) => {
        const enrolled = students.filter((student) =>
          (student.enrolments || []).some((enrol) => enrol.programme === programme.name)
        ).length;
        const attendanceRates = students
          .filter((student) => (student.enrolments || []).some((enrol) => enrol.programme === programme.name))
          .map((student) => student.attendance?.rate || 0);
        const avgAttendance = attendanceRates.length
          ? Math.round(attendanceRates.reduce((sum, value) => sum + value, 0) / attendanceRates.length)
          : 0;
        const attendanceThreshold = settings.attendanceThreshold || 70;
        return {
          id: programme.id,
          name: programme.name,
          type: 'programme',
          schedule: programme.day || (programme.days ? programme.days.join(', ') : 'TBD'),
          attendance: avgAttendance,
          staff: programme.staff || 'Unassigned',
          capacity: programme.capacity || 0,
          enrolled,
          flagged: avgAttendance > 0 && avgAttendance < attendanceThreshold
        };
      });
      const activityStats = activities.map((activity) => ({
        id: activity.id,
        name: activity.name,
        type: 'activity',
        schedule: activity.date ? `${formatDate(activity.date)} · ${activity.time || ''}` : activity.time || 'TBD',
        attendance: activity.capacity ? Math.round((activity.enrolled / activity.capacity) * 100) : 0,
        staff: activity.lead || 'Unassigned',
        capacity: activity.capacity || 0,
        enrolled: activity.enrolled || 0,
        flagged: activity.safeguarding?.consentRequired || !activity.safeguarding?.riskAssessmentComplete
      }));

      nextResults = [...programmeStats, ...activityStats];
      if (filterSettings.status !== 'all') {
        if (filterSettings.status === 'low') {
          const attendanceThreshold = settings.attendanceThreshold || 70;
          nextResults = nextResults.filter((row) => row.attendance > 0 && row.attendance < attendanceThreshold);
        }
      }
    }

    if (activeKpi === 'flagged') {
      nextResults = nextResults.filter((row) => row.flagged || row.isOverdue || row.isCritical);
    }

    setResults(nextResults);
    setHasRunReport(true);
    setSelectedIds(new Set());

    const runEntry = {
      id: `run-${Math.random().toString(36).slice(2, 8)}`,
      type,
      ranAt: new Date().toISOString(),
      params: settings
    };
    setRecentRuns((prev) => [runEntry, ...prev].slice(0, 8));
    return true;
  }, [
    reportType,
    dateRange,
    customRange,
    filters,
    showExceptionsOnly,
    actionRequiredOnly,
    incidentGroupBy,
    activeKpi,
    students,
    incidents,
    inventoryItems,
    inventoryController,
    attendanceService,
    programmes,
    activities
  ]);

  useEffect(() => {
    if (!pendingTemplateRunRef.current) return;
    const pending = pendingTemplateRunRef.current;
    if (pending.reportType !== reportType) return;
    pendingTemplateRunRef.current = null;
    applyReportSettings(pending);
    runReport(pending);
  }, [reportType, applyReportSettings, runReport]);

  const buildReportSheet = (type, rows) => {
    if (type === 'attendance') {
      return {
        name: 'Attendance',
        headers: ['Student', 'Reference', 'Programme', 'Date', 'Status', 'Attendance %', 'Risk', 'Notes'],
        rows: rows.map((row) => [
          row.studentName,
          row.reference,
          row.programme,
          formatDate(row.date),
          row.status,
          row.attendanceRate,
          row.risk,
          row.notes
        ])
      };
    }
    if (type === 'incidents') {
      return {
        name: 'Incidents',
        headers: ['Incident', 'Severity', 'Status', 'Date', 'Students', 'Follow-up', 'Owner'],
        rows: rows.map((row) => [
          row.type,
          row.severity,
          row.status,
          formatDateTime(row.date),
          row.students,
          formatDate(row.followUpDate),
          row.assignedKeyWorker
        ])
      };
    }
    if (type === 'safeguarding') {
      return {
        name: 'Safeguarding',
        headers: ['Case type', 'Severity', 'Status', 'Student', 'Owner', 'Review due'],
        rows: rows.map((row) => [
          row.type,
          row.severity,
          row.status,
          row.studentName,
          row.owner,
          formatDate(row.reviewDate)
        ])
      };
    }
    if (type === 'compliance') {
      return {
        name: 'Compliance',
        headers: ['Item', 'Area', 'Status', 'Student', 'Owner', 'Due', 'Evidence'],
        rows: rows.map((row) => [
          row.item,
          row.area,
          row.status,
          row.studentName,
          row.owner,
          formatDate(row.dueDate),
          row.evidence
        ])
      };
    }
    if (type === 'inventory') {
      return {
        name: 'Inventory',
        headers: ['Item', 'Status', 'Assignment', 'Due', 'Condition'],
        rows: rows.map((row) => [
          row.item,
          row.status,
          row.assignment,
          formatDate(row.dueDate),
          row.condition
        ])
      };
    }
    if (type === 'programmes') {
      return {
        name: 'Programmes',
        headers: ['Programme', 'Type', 'Schedule', 'Attendance', 'Staff', 'Capacity'],
        rows: rows.map((row) => [
          row.name,
          row.type,
          row.schedule,
          row.attendance,
          row.staff,
          row.capacity
        ])
      };
    }
    return { name: 'Report', headers: ['No data'], rows: [] };
  };

  const exportResults = (rows) => {
    if (!rows.length) return;
    const workbook = XLSX.utils.book_new();
    const sheet = buildReportSheet(reportType, rows);
    XLSX.utils.book_append_sheet(workbook, buildSheet(sheet.headers, sheet.rows, 'No data'), sheet.name);
    const output = XLSX.write(workbook, { bookType: 'xls', type: 'array' });
    downloadBlob(
      new Blob([output], { type: 'application/vnd.ms-excel' }),
      `report-${reportType}-${new Date().toISOString().slice(0, 10)}.xls`
    );
  };

  const exportSelected = () => {
    const rows = results.filter((row) => selectedIds.has(row.id));
    exportResults(rows);
  };

  const sanitizePath = (value) =>
    (value || 'Unknown')
      .toString()
      .trim()
      .replace(/[\\/:*?"<>|]+/g, '_')
      .replace(/\s+/g, ' ');

  const buildStudentRows = (selectedStudents) =>
    selectedStudents.map((student) => [
      student.id,
      student.name,
      student.reference,
      student.status,
      student.riskLevel,
      student.attendance?.rate || 0,
      student.keyWorker?.name || 'Unassigned',
      (student.supportWorkers || []).map((worker) => worker.name).join('; '),
      student.activeSafeguardingCount || 0,
      (student.enrolments || []).map((enrol) => enrol.programme).join('; ')
    ]);

  const buildAttendanceRows = (selectedStudents) => {
    const rows = [];
    selectedStudents.forEach((student) => {
      (student.attendance?.logs || []).forEach((log) => {
        rows.push([
          student.name,
          student.reference,
          log.programme || '',
          formatDate(log.date || log.recordedAt || ''),
          normalizeAttendanceStatus(log.status),
          student.attendance?.rate || 0,
          student.riskLevel,
          log.notes || ''
        ]);
      });
    });
    return rows;
  };

  const buildIncidentRows = (selectedStudents) => {
    const studentIds = new Set(selectedStudents.map((student) => student.id));
    return incidents
      .filter((incident) =>
        incident.participants?.some((participant) => participant.type === 'student' && studentIds.has(participant.id))
      )
      .map((incident) => [
        incident.type,
        incident.severity,
        incident.status,
        formatDateTime(incident.date),
        incident.participants
          ?.filter((participant) => participant.type === 'student')
          .map((participant) => participant.name)
          .join('; ') || '',
        formatDate(incident.followUpDate),
        incident.assignedKeyWorker || 'Unassigned'
      ]);
  };

  const buildSafeguardingRows = (selectedStudents) =>
    selectedStudents.flatMap((student) =>
      buildSafeguardingEvents(student).map((event) => [
        student.name,
        event.categoryLabel || event.category || 'Safeguarding',
        event.type || '',
        event.severity,
        event.status,
        event.assignedOwner || 'Unassigned',
        formatDate(event.reviewDate),
        formatDate(event.date)
      ])
    );

  const buildComplianceRows = (selectedStudents) =>
    selectedStudents.flatMap((student) => {
      const docs = [...(student.documents || []), ...(student.certificates || [])];
      if (!docs.length) {
        return [[student.name, 'Missing documents', 'Compliance', 'missing', student.keyWorker?.name || 'Unassigned', '', 'Missing']];
      }
      return docs.map((doc) => [
        student.name,
        doc.name,
        doc.type || doc.category || 'Policy',
        doc.status || 'compliant',
        student.keyWorker?.name || 'Unassigned',
        formatDate(doc.dueDate || doc.metadata?.dueDate || doc.expiryDate || doc.metadata?.expiryDate || ''),
        doc.versions?.length || doc.fileName ? 'Uploaded' : 'Missing'
      ]);
    });

  const buildInventoryRows = (selectedStudents, scopeAll = false) => {
    if (!selectedStudents.length && !scopeAll) return [];
    const studentNames = new Set(selectedStudents.map((student) => student.name));
    return inventoryItems
      .map((item) => {
        const assignment = inventoryController.getActiveAssignment(item);
        if (!scopeAll && (!assignment || !studentNames.has(assignment.assignedToName))) return null;
        return [
          item.reference,
          item.name,
          item.type,
          item.status,
          assignment?.assignedToName || 'Unassigned',
          formatDate(assignment?.dueDate),
          item.condition || ''
        ];
      })
      .filter(Boolean);
  };

  const buildProgrammeRows = (selectedStudents, scopeAll = false) => {
    if (scopeAll) {
      const programmeRows = programmes.map((programme) => [
        'All students',
        programme.name,
        programme.day || programme.days?.join(', ') || '',
        programme.status || '',
        programme.staff || '',
        formatDate(programme.startDate)
      ]);
      const activityRows = activities.map((activity) => [
        'All students',
        activity.name,
        activity.date ? `${formatDate(activity.date)} ${activity.time || ''}` : activity.time || '',
        activity.status || '',
        activity.lead || '',
        formatDate(activity.date)
      ]);
      return [...programmeRows, ...activityRows];
    }
    if (!selectedStudents.length) return [];
    const rows = [];
    selectedStudents.forEach((student) => {
      (student.enrolments || []).forEach((enrol) => {
        rows.push([
          student.name,
          enrol.programme,
          enrol.day || enrol.days || '',
          enrol.status || '',
          enrol.staff || '',
          formatDate(enrol.startDate)
        ]);
      });
    });
    return rows;
  };

  const collectAttachments = (selectedStudents) => {
    const files = [];
    const usedPaths = new Set();
    const ensureUniquePath = (inputPath) => {
      if (!usedPaths.has(inputPath)) {
        usedPaths.add(inputPath);
        return inputPath;
      }
      const dotIndex = inputPath.lastIndexOf('.');
      const hasExt = dotIndex > inputPath.lastIndexOf('/');
      const base = hasExt ? inputPath.slice(0, dotIndex) : inputPath;
      const ext = hasExt ? inputPath.slice(dotIndex) : '';
      let suffix = 2;
      let candidate = `${base} (${suffix})${ext}`;
      while (usedPaths.has(candidate)) {
        suffix += 1;
        candidate = `${base} (${suffix})${ext}`;
      }
      usedPaths.add(candidate);
      return candidate;
    };
    const addFile = (path, content = '') => {
      files.push({ name: ensureUniquePath(path), data: content });
    };
    const addAttachmentFile = (path, fileName, source) => {
      const safeName = sanitizePath(fileName || 'attachment.bin');
      const targetPath = `${path}/${safeName}`;
      const dataUrl =
        source?.fileUrl ||
        source?.url ||
        source?.metadata?.fileUrl ||
        source?.versions?.[0]?.fileUrl ||
        '';
      const bytes = dataUrlToBytes(dataUrl);
      if (bytes) {
        files.push({ name: ensureUniquePath(targetPath), data: bytes });
      } else {
        const note = `Source file metadata found for ${safeName}, but binary data was not available in this environment.`;
        addFile(targetPath, note);
      }
    };
    if (!includeAttachments) return files;

    addFile('Attachments/README.txt', 'Attachments are exported when inline file data exists. If binary data is unavailable, a metadata note file is exported.');

    selectedStudents.forEach((student) => {
      const basePath = `Attachments/${sanitizePath(student.name)}`;

      if (exportSections.compliance || exportSections.students) {
        const docs = [...(student.documents || []), ...(student.certificates || [])];
        docs.forEach((doc) => {
          const fileName =
            doc.fileName ||
            doc.metadata?.fileName ||
            doc.versions?.[0]?.fileName ||
            doc.name ||
            `document-${doc.id || Math.random().toString(36).slice(2, 6)}.pdf`;
          addAttachmentFile(`${basePath}/Documents`, fileName, doc);
        });
      }

      if (exportSections.safeguarding) {
        buildSafeguardingEvents(student).forEach((event) => {
          (event.attachments || []).forEach((attachment) => {
            if (typeof attachment === 'string') {
              addFile(
                `${basePath}/Safeguarding/${sanitizePath(event.type || event.id)}/${sanitizePath(attachment)}`,
                `Attachment reference: ${attachment}`
              );
              return;
            }
            const fileName = attachment?.name || attachment?.fileName || `event-${event.id}`;
            addAttachmentFile(
              `${basePath}/Safeguarding/${sanitizePath(event.type || event.id)}`,
              fileName,
              attachment
            );
          });
        });
      }

      if (exportSections.incidents) {
        const studentIncidentDocs = incidents.filter((incident) =>
          incident.participants?.some((participant) => participant.type === 'student' && participant.id === student.id)
        );
        studentIncidentDocs.forEach((incident) => {
          (incident.documents || []).forEach((doc) => {
            const fileName = doc.name || doc.fileName || `incident-${incident.id}.pdf`;
            addAttachmentFile(
              `${basePath}/Incidents/${sanitizePath(incident.type || incident.id)}`,
              fileName,
              doc
            );
          });
        });
      }

      if (exportSections.students || exportSections.programmes) {
        const journalEntries = journalService.listForStudent(student.id);
        journalEntries.forEach((entry) => {
          (entry.attachments || []).forEach((attachment) => {
            if (typeof attachment === 'string') {
              addFile(
                `${basePath}/Journal/${sanitizePath(entry.title || entry.id)}/${sanitizePath(attachment)}`,
                `Attachment reference: ${attachment}`
              );
              return;
            }
            const fileName = attachment?.name || attachment?.fileName || `journal-${entry.id}.bin`;
            addAttachmentFile(
              `${basePath}/Journal/${sanitizePath(entry.title || entry.id)}`,
              fileName,
              attachment
            );
          });
        });

        const portalMessages = externalMessageService
          .listForStudent(student.id)
          .filter((message) => Array.isArray(message.attachments) && message.attachments.length > 0);
        portalMessages.forEach((message) => {
          (message.attachments || []).forEach((attachment) => {
            if (typeof attachment === 'string') {
              addFile(
                `${basePath}/External Messages/${sanitizePath(message.subject || message.id)}/${sanitizePath(attachment)}`,
                `Attachment reference: ${attachment}`
              );
              return;
            }
            const fileName = attachment?.name || attachment?.fileName || `message-${message.id}.bin`;
            addAttachmentFile(
              `${basePath}/External Messages/${sanitizePath(message.subject || message.id)}`,
              fileName,
              attachment
            );
          });
        });

        const assignments = assignmentService.listForStudents([student.id]);
        assignments.forEach((assignment) => {
          const assignmentPath = `${basePath}/Assignments/${sanitizePath(assignment.title || assignment.id)}`;
          (assignment.attachments || []).forEach((attachment) => {
            if (typeof attachment === 'string') {
              addFile(`${assignmentPath}/${sanitizePath(attachment)}`, `Attachment reference: ${attachment}`);
              return;
            }
            const fileName = attachment?.name || attachment?.fileName || `assignment-${assignment.id}.bin`;
            addAttachmentFile(assignmentPath, fileName, attachment);
          });
          (assignment.timeline || []).forEach((entry) => {
            (entry.attachments || []).forEach((attachment) => {
              if (typeof attachment === 'string') {
                addFile(
                  `${assignmentPath}/Timeline/${sanitizePath(entry.type || entry.id)}/${sanitizePath(attachment)}`,
                  `Attachment reference: ${attachment}`
                );
                return;
              }
              const fileName = attachment?.name || attachment?.fileName || `timeline-${entry.id}.bin`;
              addAttachmentFile(
                `${assignmentPath}/Timeline/${sanitizePath(entry.type || entry.id)}`,
                fileName,
                attachment
              );
            });
          });
        });
      }
    });

    return files;
  };

  const exportAllSections = () => {
    const selectedStudents =
      exportScope === 'student'
        ? students.filter((student) => student.id === exportStudentId)
        : students;

    if (!selectedStudents.length) return;

    const workbook = XLSX.utils.book_new();

    if (exportSections.students) {
      const headers = ['Student ID', 'Name', 'Reference', 'Status', 'Risk', 'Attendance %', 'Key worker', 'Support workers', 'Safeguarding active', 'Programmes'];
      XLSX.utils.book_append_sheet(
        workbook,
        buildSheet(headers, buildStudentRows(selectedStudents), 'No students'),
        'Students'
      );
    }

    if (exportSections.attendance) {
      const headers = ['Student', 'Reference', 'Programme', 'Date', 'Status', 'Attendance %', 'Risk', 'Notes'];
      XLSX.utils.book_append_sheet(
        workbook,
        buildSheet(headers, buildAttendanceRows(selectedStudents), 'No attendance logs'),
        'Attendance'
      );
    }

    if (exportSections.incidents) {
      const headers = ['Incident', 'Severity', 'Status', 'Date', 'Students', 'Follow-up', 'Owner'];
      XLSX.utils.book_append_sheet(
        workbook,
        buildSheet(headers, buildIncidentRows(selectedStudents), 'No incidents'),
        'Incidents'
      );
    }

    if (exportSections.safeguarding) {
      const headers = ['Student', 'Category', 'Type', 'Severity', 'Status', 'Owner', 'Review due', 'Logged'];
      XLSX.utils.book_append_sheet(
        workbook,
        buildSheet(headers, buildSafeguardingRows(selectedStudents), 'No safeguarding records'),
        'Safeguarding'
      );
    }

    if (exportSections.compliance) {
      const headers = ['Student', 'Item', 'Area', 'Status', 'Owner', 'Due / expiry', 'Evidence'];
      XLSX.utils.book_append_sheet(
        workbook,
        buildSheet(headers, buildComplianceRows(selectedStudents), 'No compliance records'),
        'Compliance'
      );
    }

    if (exportSections.inventory) {
      const headers = ['Reference', 'Item', 'Type', 'Status', 'Assigned to', 'Due', 'Condition'];
      XLSX.utils.book_append_sheet(
        workbook,
        buildSheet(
          headers,
          buildInventoryRows(selectedStudents, exportScope === 'all'),
          'No inventory assignments'
        ),
        'Inventory'
      );
    }

    if (exportSections.programmes) {
      const headers = ['Student', 'Programme', 'Schedule', 'Status', 'Staff', 'Start date'];
      XLSX.utils.book_append_sheet(
        workbook,
        buildSheet(
          headers,
          buildProgrammeRows(selectedStudents, exportScope === 'all'),
          'No programmes'
        ),
        'Programmes'
      );
    }

    const output = XLSX.write(workbook, { bookType: 'xls', type: 'array' });
    const reportName = `report-${exportScope === 'student' ? sanitizePath(selectedStudents[0].name) : 'all'}-${new Date().toISOString().slice(0, 10)}`;

    if (includeAttachments) {
      const files = collectAttachments(selectedStudents);
      const zipFiles = [
        { name: `${reportName}.xls`, data: new Uint8Array(output) },
        ...files
      ];
      const zipBlob = buildZip(zipFiles);
      downloadBlob(zipBlob, `${reportName}.zip`);
    } else {
      downloadBlob(new Blob([output], { type: 'application/vnd.ms-excel' }), `${reportName}.xls`);
    }

    setExportModalOpen(false);
  };

  const activeResult = results.find((row) => row.id === activeResultId);
  const openResultDrawer = (rowId) => {
    setActiveResultId(rowId);
    setDrawerOpen(true);
  };

  const groupedIncidentResults = useMemo(() => {
    if (reportType !== 'incidents') return results.map((row) => ({ type: 'row', row }));
    if (incidentGroupBy === 'date') {
      const grouped = {};
      results.forEach((row) => {
        const key = row.date || 'Unknown';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(row);
      });
      return Object.entries(grouped).flatMap(([key, rows]) => [
        { type: 'group', label: key === 'Unknown' ? key : formatDateTime(key) },
        ...rows.map((row) => ({ type: 'row', row }))
      ]);
    }
    if (incidentGroupBy === 'type') {
      const grouped = {};
      results.forEach((row) => {
        const key = row.type || 'Other';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(row);
      });
      return Object.entries(grouped).flatMap(([key, rows]) => [
        { type: 'group', label: key },
        ...rows.map((row) => ({ type: 'row', row }))
      ]);
    }
    if (incidentGroupBy === 'student') {
      const grouped = {};
      results.forEach((row) => {
        const key = row.students || 'Unassigned';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(row);
      });
      return Object.entries(grouped).flatMap(([key, rows]) => [
        { type: 'group', label: key },
        ...rows.map((row) => ({ type: 'row', row }))
      ]);
    }
    return results.map((row) => ({ type: 'row', row }));
  }, [results, reportType, incidentGroupBy]);

  const resultRows = reportType === 'incidents' ? groupedIncidentResults : results.map((row) => ({ type: 'row', row }));

  const saveTemplate = () => {
    if (!templateName.trim()) return;
    setSavedTemplates((prev) => [
      {
        id: `tpl-${Math.random().toString(36).slice(2, 8)}`,
        name: templateName.trim(),
        type: reportType,
        savedAt: new Date().toISOString(),
        owner: 'You',
        settings: {
          reportType,
          dateRange,
          customRange,
          filters,
          showExceptionsOnly,
          actionRequiredOnly,
          incidentGroupBy
        }
      },
      ...prev
    ]);
    setTemplateName('');
    setSaveModalOpen(false);
  };

  const runTemplate = (templateOrRun) => {
    const normalized = normalizeRunSettings(
      templateOrRun.settings || templateOrRun.params || {},
      templateOrRun.type || reportType
    );
    if (normalized.reportType !== reportType) {
      pendingTemplateRunRef.current = normalized;
      setReportType(normalized.reportType);
      return;
    }
    applyReportSettings(normalized);
    runReport(normalized);
  };

  const duplicateTemplate = (template) => {
    const copy = {
      ...template,
      id: `tpl-${Math.random().toString(36).slice(2, 8)}`,
      name: `${template.name} Copy`,
      savedAt: new Date().toISOString()
    };
    setSavedTemplates((prev) => [copy, ...prev]);
  };

  const showProgrammeFilter = ['attendance', 'inventory', 'programmes'].includes(reportType);
  const showStudentFilter = ['attendance', 'incidents', 'safeguarding', 'compliance'].includes(reportType);
  const showStaffFilter = ['incidents', 'safeguarding', 'compliance', 'programmes', 'inventory'].includes(reportType);
  const showRiskFilter = ['attendance', 'incidents', 'safeguarding'].includes(reportType);
  const showStatusFilter = ['attendance', 'incidents', 'safeguarding', 'compliance', 'inventory', 'programmes'].includes(reportType);
  const hasActiveFilters = Boolean(
    filters.programmes.length ||
    filters.students.length ||
    filters.staff ||
    filters.risk !== 'all' ||
    filters.status !== 'all' ||
    dateRange !== defaultReportRange ||
    (dateRange === 'custom' && (customRange.start || customRange.end))
  );

  return (
    <div className="space-y-6">
      <div className="sp-page-header">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Reports</h2>
          <p className="sp-muted">Run and export audit-ready reports with fast filtering.</p>
        </div>
        <div className="sp-page-actions">
          <Button
            size="sm"
            onClick={() => {
              setReportType('attendance');
              setDateRange(defaultReportRange);
              setCustomRange({ start: '', end: '' });
              setFilters(defaultFilters);
              setShowExceptionsOnly(true);
              setActionRequiredOnly(true);
              setIncidentGroupBy('date');
              setResults([]);
              setSelectedIds(new Set());
              setActiveResultId(null);
              setDrawerOpen(false);
              setActiveKpi(null);
              setHasRunReport(false);
              setReportError('');
            }}
          >
            + New Report
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              resetExportState();
              setExportModalOpen(true);
            }}
          >
            Export Centre Summary
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings?tab=templates')}>Manage templates</Button>
        </div>
      </div>

      <WorkspaceKpis items={kpiItems} activeKpi={activeKpi} onSelect={handleKpiSelect} />

      <div className="sp-filter-bar">
        <div className="sp-filter-main">
          <div>
            <label className="sp-label">Report type</label>
            <select
              className="sp-select mt-2 w-full"
              aria-label="Report type"
              value={reportType}
              onChange={(event) => setReportType(event.target.value)}
            >
              {reportTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="sp-filter-controls">
            <div>
              <label className="sp-label">Date range</label>
              <select
                className="sp-select mt-2"
                aria-label="Date range"
                value={dateRange}
                onChange={(event) => {
                  setDateRange(event.target.value);
                  setReportError('');
                }}
              >
                {dateRanges.map((range) => (
                  <option key={range.id} value={range.id}>{range.label}</option>
                ))}
              </select>
            </div>
            {dateRange === 'custom' && (
              <div className="sp-filter-controls">
                <div>
                  <label className="sp-label">Start</label>
                  <DateSelect
                    value={customRange.start}
                    onChange={(value) => {
                      setCustomRange((prev) => ({ ...prev, start: value }));
                      setReportError('');
                    }}
                    className="mt-2"
                  />
                </div>
                <div>
                  <label className="sp-label">End</label>
                  <DateSelect
                    value={customRange.end}
                    onChange={(value) => {
                      setCustomRange((prev) => ({ ...prev, end: value }));
                      setReportError('');
                    }}
                    className="mt-2"
                  />
                </div>
              </div>
            )}
            {showProgrammeFilter && (
              <div>
                <label className="sp-label">Programme</label>
                <SearchableSelect
                  value={filters.programmes[0] || ''}
                  options={programmeOptions}
                  placeholder="Select programme"
                  emptyLabel="No programmes found"
                  onChange={(value) => setFilters((prev) => ({ ...prev, programmes: value ? [value] : [] }))}
                />
              </div>
            )}
            {showStudentFilter && (
              <details className="sp-dropdown">
                <summary className="sp-select">{filters.students.length ? `${filters.students.length} students` : 'Students'}</summary>
                <div className="sp-panel sp-panel--muted p-3 mt-2">
                  <SearchableChecklist
                    items={studentOptions}
                    selectedIds={filters.students}
                    getId={(item) => item.value}
                    getLabel={(item) => item.label}
                    getMeta={(item) => item.meta}
                    onToggle={(id) => setFilters((prev) => ({
                      ...prev,
                      students: prev.students.includes(id)
                        ? prev.students.filter((item) => item !== id)
                        : [...prev.students, id]
                    }))}
                    placeholder="Search students"
                    emptyLabel="No students found"
                  />
                </div>
              </details>
            )}
            {showStaffFilter && (
              <div>
                <label className="sp-label">Support worker</label>
                <SearchableSelect
                  value={filters.staff}
                  options={staffOptions}
                  placeholder="Select staff"
                  emptyLabel="No staff found"
                  onChange={(value) => setFilters((prev) => ({ ...prev, staff: value }))}
                />
              </div>
            )}
            {showRiskFilter && (
              <div>
                <label className="sp-label">Risk / Severity</label>
                <select
                  className="sp-select mt-2"
                  aria-label="Risk / Severity"
                  value={filters.risk}
                  onChange={(event) => setFilters((prev) => ({ ...prev, risk: event.target.value }))}
                >
                  <option value="all">All</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            )}
            {showStatusFilter && (
              <div>
                <label className="sp-label">Status</label>
                <select
                  className="sp-select mt-2"
                  aria-label="Status"
                  value={filters.status}
                  onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                >
                  <option value="all">All</option>
                  {reportType === 'attendance' && (
                    <>
                      <option value="attended">Attended</option>
                      <option value="late">Late</option>
                      <option value="absent">Absent</option>
                      <option value="unauthorised">Unauthorised</option>
                    </>
                  )}
                  {reportType === 'incidents' && (
                    <>
                      <option value="open">Open</option>
                      <option value="closed">Closed</option>
                    </>
                  )}
                  {reportType === 'safeguarding' && (
                    <>
                      <option value="open">Open</option>
                      <option value="monitoring">Monitoring</option>
                      <option value="closed">Closed</option>
                    </>
                  )}
                  {reportType === 'compliance' && (
                    <>
                      <option value="overdue">Overdue</option>
                      <option value="due-soon">Due soon</option>
                      <option value="missing">Missing evidence</option>
                    </>
                  )}
                  {reportType === 'inventory' && (
                    <>
                      <option value="overdue">Overdue</option>
                      <option value="under_repair">Under repair</option>
                      <option value="unassigned">Unassigned</option>
                    </>
                  )}
                  {reportType === 'programmes' && (
                    <>
                      <option value="low">Low attendance</option>
                    </>
                  )}
                </select>
              </div>
            )}
          </div>
          <div className="sp-filter-controls">
            <Button size="sm" onClick={() => runReport()}>Run report</Button>
            <Button size="sm" variant="secondary" onClick={() => setSaveModalOpen(true)}>Save template</Button>
            {hasActiveFilters && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDateRange(defaultReportRange);
                  setCustomRange({ start: '', end: '' });
                  setFilters(defaultFilters);
                  setShowExceptionsOnly(true);
                  setActionRequiredOnly(true);
                  setIncidentGroupBy('date');
                  setReportError('');
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="sp-workspace-grid">
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {reportType === 'attendance' && (
                <Button size="sm" variant={showExceptionsOnly ? 'secondary' : 'ghost'} onClick={() => setShowExceptionsOnly((prev) => !prev)}>
                  {showExceptionsOnly ? 'Exceptions only' : 'Show all'}
                </Button>
              )}
              {reportType === 'incidents' && (
                <select
                  className="sp-select"
                  aria-label="Incident grouping"
                  value={incidentGroupBy}
                  onChange={(event) => setIncidentGroupBy(event.target.value)}
                >
                  <option value="date">Group by date</option>
                  <option value="student">Group by student</option>
                  <option value="type">Group by incident type</option>
                </select>
              )}
              {reportType === 'safeguarding' && (
                <Button size="sm" variant={actionRequiredOnly ? 'secondary' : 'ghost'} onClick={() => setActionRequiredOnly((prev) => !prev)}>
                  {actionRequiredOnly ? 'Action required only' : 'Show all'}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasRunReport && <span className="text-xs sp-muted">{results.length} results</span>}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  resetExportState();
                  setExportModalOpen(true);
                }}
              >
                Export
              </Button>
              <Button size="sm" variant="ghost" onClick={() => window.print()} disabled={!results.length}>
                Print
              </Button>
            </div>
          </div>

          {reportError ? (
            <Card className="p-6">
              <p className="text-sm" style={{ color: 'var(--sp-red)' }}>{reportError}</p>
            </Card>
          ) : !results.length ? (
            <Card className="p-6">
              <p className="text-sm sp-muted">
                {hasRunReport ? 'No results for current filters.' : 'Run a report to see results.'}
              </p>
            </Card>
          ) : (
            <div className={`sp-table sp-table--reports-${reportType}`}>
              <div className="sp-row sp-row--header">
                <div>
                  <input
                    type="checkbox"
                    checked={selectedIds.size > 0 && selectedIds.size === results.length}
                    onChange={(event) => {
                      if (event.target.checked) setSelectedIds(new Set(results.map((row) => row.id)));
                      else setSelectedIds(new Set());
                    }}
                    aria-label="Select all"
                  />
                </div>
                {reportType === 'attendance' && (
                  <>
                    <div>Student</div>
                    <div>Programme</div>
                    <div>Status</div>
                    <div>Date</div>
                    <div>Attendance %</div>
                    <div>Risk</div>
                    <div>Actions</div>
                  </>
                )}
                {reportType === 'incidents' && (
                  <>
                    <div>Incident</div>
                    <div>Severity</div>
                    <div>Status</div>
                    <div>Students</div>
                    <div>Follow-up</div>
                    <div>Owner</div>
                    <div>Actions</div>
                  </>
                )}
                {reportType === 'safeguarding' && (
                  <>
                    <div>Case type</div>
                    <div>Risk</div>
                    <div>Status</div>
                    <div>Student</div>
                    <div>Owner</div>
                    <div>Review due</div>
                    <div>Actions</div>
                  </>
                )}
                {reportType === 'compliance' && (
                  <>
                    <div>Item</div>
                    <div>Area</div>
                    <div>Status</div>
                    <div>Student</div>
                    <div>Due / expiry</div>
                    <div>Evidence</div>
                    <div>Actions</div>
                  </>
                )}
                {reportType === 'inventory' && (
                  <>
                    <div>Item</div>
                    <div>Status</div>
                    <div>Assignment</div>
                    <div>Due</div>
                    <div>Condition</div>
                    <div>Actions</div>
                  </>
                )}
                {reportType === 'programmes' && (
                  <>
                    <div>Programme</div>
                    <div>Schedule</div>
                    <div>Attendance %</div>
                    <div>Staff</div>
                    <div>Capacity</div>
                    <div>Actions</div>
                  </>
                )}
              </div>
              {resultRows.map((entry) => {
                if (entry.type === 'group') {
                  return (
                    <div key={`group-${entry.label}`} className="sp-row sp-row--group">
                      <div style={{ gridColumn: '1 / -1' }} className="text-xs sp-muted">{entry.label}</div>
                    </div>
                  );
                }
                const row = entry.row;
                const isSelected = selectedIds.has(row.id);
                const rowClass = row.flagged || row.isOverdue || row.isCritical ? 'sp-row--warning' : '';
                return (
                  <div
                    key={row.id}
                    className={`sp-row sp-row--inventory ${rowClass}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open report row ${row.studentName || row.item || row.type || row.name}`}
                    onClick={() => openResultDrawer(row.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openResultDrawer(row.id);
                      }
                    }}
                  >
                    <div onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(row.id)) next.delete(row.id);
                            else next.add(row.id);
                            return next;
                          });
                        }}
                        aria-label="Select row"
                      />
                    </div>
                    {reportType === 'attendance' && (
                      <>
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{row.studentName}</div>
                          <div className="text-xs sp-muted">{row.reference}</div>
                        </div>
                        <div>{row.programme}</div>
                        <div><Badge variant={row.status === 'unauthorised' ? 'danger' : row.status === 'absent' ? 'warning' : 'active'}>{row.status}</Badge></div>
                        <div className="text-sm">{formatDate(row.date)}</div>
                        <div>{row.attendanceRate}%</div>
                        <div><Badge variant={row.risk === 'critical' ? 'critical' : row.risk === 'high' ? 'high' : 'medium'}>{row.risk}</Badge></div>
                        <div><Button size="sm" variant="ghost" onClick={(event) => { event.stopPropagation(); navigate(`/students/${row.studentId}/profile`); }}>View</Button></div>
                      </>
                    )}
                    {reportType === 'incidents' && (
                      <>
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{row.type}</div>
                          <div className="text-xs sp-muted">{formatDateTime(row.date)}</div>
                        </div>
                        <div><Badge variant={row.severity === 'critical' ? 'critical' : row.severity === 'high' ? 'high' : 'medium'}>{row.severity}</Badge></div>
                        <div><Badge variant={row.status === 'open' ? 'warning' : 'inactive'}>{row.status}</Badge></div>
                        <div>{row.students || 'Unassigned'}</div>
                        <div>{row.followUpOverdue ? 'Overdue' : formatDate(row.followUpDate) || '—'}</div>
                        <div>{row.assignedKeyWorker}</div>
                        <div><Button size="sm" variant="ghost" onClick={(event) => { event.stopPropagation(); navigate(`/incidents/${row.id}`); }}>View</Button></div>
                      </>
                    )}
                    {reportType === 'safeguarding' && (
                      <>
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{row.type}</div>
                          <div className="text-xs sp-muted">{row.category}</div>
                        </div>
                        <div><Badge variant={row.severity === 'critical' ? 'critical' : row.severity === 'high' ? 'high' : 'medium'}>{row.severity}</Badge></div>
                        <div><Badge variant={row.status === 'open' ? 'warning' : row.status === 'monitoring' ? 'review' : 'inactive'}>{row.status}</Badge></div>
                        <div>{row.studentName}</div>
                        <div>{row.owner}</div>
                        <div>{formatDate(row.reviewDate) || '—'}</div>
                        <div><Button size="sm" variant="ghost" onClick={(event) => { event.stopPropagation(); navigate(`/students/${row.studentId}/profile`); }}>View</Button></div>
                      </>
                    )}
                    {reportType === 'compliance' && (
                      <>
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{row.item}</div>
                          <div className="text-xs sp-muted">{row.studentName}</div>
                        </div>
                        <div>{row.area}</div>
                        <div><Badge variant={row.status === 'overdue' || row.status === 'missing' ? 'danger' : 'warning'}>{row.status}</Badge></div>
                        <div>{row.studentName}</div>
                        <div>{formatDate(row.dueDate) || '—'}</div>
                        <div>{row.evidence}</div>
                        <div><Button size="sm" variant="ghost" onClick={(event) => { event.stopPropagation(); navigate(`/students/${row.studentId}/profile`); }}>View</Button></div>
                      </>
                    )}
                    {reportType === 'inventory' && (
                      <>
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{row.item}</div>
                          <div className="text-xs sp-muted">{row.assignment}</div>
                        </div>
                        <div><Badge variant={row.isOverdue ? 'danger' : row.status === 'under_repair' ? 'inactive' : 'active'}>{row.status}</Badge></div>
                        <div>{row.assignment}</div>
                        <div>{formatDate(row.dueDate) || '—'}</div>
                        <div>{row.condition}</div>
                        <div><Button size="sm" variant="ghost" onClick={(event) => { event.stopPropagation(); navigate(`/inventory/${row.id}`); }}>View</Button></div>
                      </>
                    )}
                    {reportType === 'programmes' && (
                      <>
                        <div>
                          <div className="sp-value" style={{ fontWeight: 600 }}>{row.name}</div>
                          <div className="text-xs sp-muted">{row.type}</div>
                        </div>
                        <div>{row.schedule}</div>
                        <div>{row.attendance}%</div>
                        <div>{row.staff}</div>
                        <div>{row.enrolled || 0}/{row.capacity || '—'}</div>
                        <div><Button size="sm" variant="ghost" onClick={(event) => { event.stopPropagation(); navigate(row.type === 'activity' ? `/activities/${row.id}/edit` : `/programmes/overview/${row.id}/edit`); }}>View</Button></div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Card className={`sp-workspace-drawer ${drawerOpen ? 'is-open' : ''}`}>
          {!activeResult ? (
            <div className="sp-drawer-empty">Select a row to view details.</div>
          ) : (
            <div className="space-y-4">
              <div className="sp-drawer-header">
                <div>
                  <div className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>{activeResult.studentName || activeResult.item || activeResult.type || activeResult.name}</div>
                  <div className="text-xs sp-muted">{reportType.toUpperCase()}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setDrawerOpen(false)}>Close</Button>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-drawer-meta">
                  {activeResult.status && (
                    <div>
                      <div className="sp-label">Status</div>
                      <div className="sp-value">{activeResult.status}</div>
                    </div>
                  )}
                  {activeResult.severity && (
                    <div>
                      <div className="sp-label">Severity</div>
                      <div className="sp-value">{activeResult.severity}</div>
                    </div>
                  )}
                  {activeResult.reviewDate && (
                    <div>
                      <div className="sp-label">Review due</div>
                      <div className="sp-value">{formatDate(activeResult.reviewDate)}</div>
                    </div>
                  )}
                  {activeResult.dueDate && (
                    <div>
                      <div className="sp-label">Due / expiry</div>
                      <div className="sp-value">{formatDate(activeResult.dueDate)}</div>
                    </div>
                  )}
                  {activeResult.assignment && (
                    <div>
                      <div className="sp-label">Assignment</div>
                      <div className="sp-value">{activeResult.assignment}</div>
                    </div>
                  )}
                </div>
              </div>
              {activeResult.signals && activeResult.signals.length > 0 && (
                <div className="sp-drawer-section">
                  <div className="sp-label">Safeguarding signals</div>
                  <div className="space-y-2">
                    {activeResult.signals.map((signal) => (
                      <div key={signal.id} className="sp-alert sp-alert--high text-sm">
                        <Icons.Alert /> {signal.text}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="sp-drawer-actions">
                {activeResult.studentId && (
                  <Button size="sm" onClick={() => navigate(`/students/${activeResult.studentId}/profile`)}>
                    Open student profile
                  </Button>
                )}
                {activeResult.id && reportType === 'incidents' && (
                  <Button size="sm" variant="secondary" onClick={() => navigate(`/incidents/${activeResult.id}`)}>
                    Open incident
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      {drawerOpen && <div className="sp-drawer-overlay" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}

      <BulkActionBar
        selectedCount={selectedIds.size}
        actions={[
          { label: 'Export selected', onClick: exportSelected, disabled: selectedIds.size === 0 }
        ]}
      />

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Saved reports & recent runs</h3>
            <p className="text-sm sp-muted">Reuse templates and rerun audits quickly.</p>
          </div>
          <div className="sp-segment" role="tablist" aria-label="Report tabs">
            <button type="button" role="tab" className={savedTab === 'saved' ? 'active' : ''} onClick={() => setSavedTab('saved')}>
              Saved
            </button>
            <button type="button" role="tab" className={savedTab === 'recent' ? 'active' : ''} onClick={() => setSavedTab('recent')}>
              Recent runs
            </button>
          </div>
        </div>
        {savedTab === 'saved' ? (
          savedTemplates.length === 0 ? (
            <p className="text-sm sp-muted">No saved templates yet.</p>
          ) : (
            <div className="sp-table sp-table--reports">
              <div className="sp-row sp-row--header">
                <div>Name</div>
                <div>Type</div>
                <div>Last run</div>
                <div>Actions</div>
              </div>
              {savedTemplates.map((template) => (
                <div key={template.id} className="sp-row sp-row--student">
                  <div>
                    <div className="sp-value" style={{ fontWeight: 600 }}>{template.name}</div>
                    <div className="text-xs sp-muted">{template.owner}</div>
                  </div>
                  <div>{template.type}</div>
                  <div className="text-sm sp-muted">{formatDate(template.savedAt)}</div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="secondary" onClick={() => runTemplate(template)}>Run</Button>
                    <Button size="sm" variant="ghost" onClick={() => duplicateTemplate(template)}>Duplicate</Button>
                    <Button size="sm" variant="ghost" onClick={() => setSavedTemplates((prev) => prev.filter((item) => item.id !== template.id))}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          recentRuns.length === 0 ? (
            <p className="text-sm sp-muted">No recent runs recorded.</p>
          ) : (
            <div className="sp-table sp-table--reports">
              <div className="sp-row sp-row--header">
                <div>Timestamp</div>
                <div>Type</div>
                <div>Parameters</div>
                <div>Action</div>
              </div>
              {recentRuns.map((run) => (
                <div key={run.id} className="sp-row sp-row--student">
                  <div className="text-sm sp-muted">{formatDateTime(run.ranAt)}</div>
                  <div>{run.type}</div>
                  <div className="text-xs sp-muted">{run.params?.dateRange} · {run.params?.filters?.status || 'all'}</div>
                  <div>
                    <Button size="sm" variant="secondary" onClick={() => runTemplate(run)}>Run</Button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </Card>

      <Modal isOpen={exportModalOpen} onClose={() => setExportModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Export reports</h3>
              <p className="text-sm sp-muted">Export student data with tabs for each selected section.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setExportModalOpen(false)}>Close</Button>
          </div>

          <div>
            <label className="sp-label">Scope</label>
            <div className="sp-segment mt-2" role="tablist" aria-label="Export scope">
              <button
                type="button"
                role="tab"
                aria-selected={exportScope === 'all'}
                className={exportScope === 'all' ? 'active' : ''}
                onClick={() => setExportScope('all')}
              >
                All students
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={exportScope === 'student'}
                className={exportScope === 'student' ? 'active' : ''}
                onClick={() => setExportScope('student')}
              >
                Single student
              </button>
            </div>
          </div>

          {exportScope === 'student' && (
            <div>
              <label className="sp-label">Student</label>
              <SearchableSelect
                value={exportStudentId}
                options={studentOptions}
                placeholder="Search student"
                emptyLabel="No students found"
                onChange={setExportStudentId}
                ariaLabel="Select student for export"
              />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <label className="sp-label">Sections</label>
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => setAllExportSections(!allExportSelected)}
              >
                {allExportSelected ? 'Clear all' : 'Select all'}
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-2 mt-2">
              {exportSectionOptions.map((option) => (
                <label key={option.id} className="flex items-center gap-2 text-sm sp-muted">
                  <input
                    type="checkbox"
                    checked={exportSections[option.id]}
                    onChange={() => toggleExportSection(option.id)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="sp-label">Attachments</label>
            <label className="sp-inline-toggle mt-2">
              <input
                type="checkbox"
                checked={includeAttachments}
                onChange={(event) => setIncludeAttachments(event.target.checked)}
              />
              Include attachments in a ZIP (grouped by student and record type)
            </label>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setExportModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={exportAllSections}
              disabled={
                (exportScope === 'student' && !exportStudentId) ||
                !exportSectionOptions.some((option) => exportSections[option.id])
              }
            >
              Export
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={saveModalOpen} onClose={() => setSaveModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Save report template</h3>
              <p className="text-sm sp-muted">Store current filters for quick reuse.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSaveModalOpen(false)}>Close</Button>
          </div>
          <div>
            <label className="sp-label">Template name</label>
            <input
              className="sp-input w-full mt-2"
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              placeholder="e.g. Weekly safeguarding audit"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setSaveModalOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={!templateName.trim()}>Save template</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
